<script lang="ts" setup>
import type { HeaderGradeTypeProps } from '~/models/Subject'
import type { ColorName } from '~/models/Content/Color'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { faArrowLeft } from '@fortawesome/duotone-regular-svg-icons'
import { useRoute, useRouter } from '#imports'
import { firstOf } from '~/utils/queryUtils'
import { colorMap } from '~/utils/colors'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { ColorTheme } from '~/models/Content/Color'
import { useScrollBehavior } from '~/composables/useScrollBehavior'
import { useMedia } from '~/composables/useMedia'
import { useContentHelper } from '~/composables/useContentHelper'
import { useMonoColorTheme } from '~/composables/useColorTheme'
import { useAppColor } from '~/composables/useAppColor'
import useAccordion from '~/composables/useAccordion'
import ScrollButton from '~/components/utils/ScrollButton.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import HeaderViewBannerGrid from '~/components/utils/HeaderViewBannerGrid.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import HeaderTeacherButton from '~/components/subject/HeaderTeacherButton.vue'
import HeaderStencil from '~/components/subject/HeaderStencil.vue'
import Header1To4Section from '~/components/subject/gradetypes/Header1To4Section.vue'
import GradeFilter from '~/components/ResourceFinder/GradeFilter.vue'
import Image from '~/components/media/Image.vue'
import ColorCard from '~/components/cards/ColorCard.vue'

const props = withDefaults(defineProps<HeaderGradeTypeProps>(), {
  subjectRoute: 'subject',
  isLoading: false,
  gradeFilter: false,
})

useScrollBehavior()

const { t } = useI18n()
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())
const { isTeacher } = storeToRefs(useAuthStore())
const { set: setAppColor, clear: clearAppColor } = useAppColor()
const { isContentHeader } = useContentHelper()
const { getAccordion } = useAccordion()

const route = useRoute()
const router = useRouter()

const mediaContentId = computed(() => Number(props.header?.backgroundMedia?.destinationContentId) > 0
  ? [Number(props.header?.backgroundMedia?.destinationContentId)]
  : [])

const { data: bgMedia, isLoading: bgMediaIsLoading } = firstOf(useMedia(mediaContentId))

const { theme } = useMonoColorTheme(props.header?.colorTheme ?? ColorTheme.BlueOrange)
const skeletonColor = computed(() => theme.value[20])
const dropdownBorderColor = computed(() => theme.value[60])
const isCollapsible = computed(() => Boolean(props.header && isContentHeader(props.header) && props.header.collapsible))
const showGradeFilter = computed(() => props.gradeFilter && (props.header?.grades?.length ?? 0) > 1)

const showTeacherButton = computed(() => isTeacher.value
  && hasLoadedProducts.value
  && props.header
)

const bannerColor = computed(() => {
  const backgroundColor = props.header?.backgroundColor as ColorName
  return `background: ${colorMap.get(backgroundColor)}`
})

const headerStyle = computed(() => {
  const id = props.header?.stencil?.identifier
  const backgroundColor = props.header?.backgroundColor as ColorName
  return (id && id !== 'none') ? colorMap.get(backgroundColor) : ''
})

const sectionColors = computed(() => {
  if (!props.packages.length) {
    return `background-color: ${theme.value[50]}; color: ${theme.value[20]};`
  } else {
    return props.packages.map((_, index) => {
      const backgroundColor = index % 2 === 0 ? theme.value[60] : theme.value[50]
      const textColor = theme.value.pop

      return `background-color: ${backgroundColor}; color: ${textColor};`
    })
  }
})

watch(props, () => {
  if (!props.header?.backgroundColor)
    return
  setAppColor(props.header.backgroundColor as ColorName)
}, { immediate: true })

onMounted(() => {
  if (props.header?.backgroundColor) {
    setAppColor(props.header.backgroundColor as ColorName)
  }
})

onBeforeUnmount(() => clearAppColor())

watch(() => props.header, () => {
  if (!props.header)
    return
  updateAccordion(props.header.locationId)
}, { immediate: true, deep: true })

function updateAccordion(headerLocationId: number) {
  const [locationId] = getAccordion(headerLocationId)
  const locationIndex = locationId
    ? props.packages.findIndex(content => locationId === content.locationId)
    : -1

  const accordionNr = Number(route.hash || (locationIndex + 1))
  if (accordionNr < 1)
    return

  router
    .replace({ ...route, hash: `#${accordionNr}` })
    .then(() => nextTick())
    .then(() => {
      setTimeout(() => {
        document
          .getElementById(`${accordionNr}`)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
      }, 100) // wait for transition of accordion
    })
}
</script>

<template>
  <HeaderViewBannerGrid :style="bannerColor">
    <h1
      v-if="props.header"
      class="sr-only"
      v-text="props.header.title"
    />

    <Teleport to="#header-navigation">
      <Button
        :to="{ name: props.subjectRoute, params: { subject: subjectCode.toLowerCase() } }"
        :color="props.header?.backgroundColor"
        size="xl"
        shade="light"
      >
        <font-awesome-icon :icon="faArrowLeft" />
        {{ t(`metadata.subjects.${subjectCode}`) }}
      </Button>

      <AuSkeleton
        v-if="isLoadingProduct"
        background="#fff7"
        class="!rounded-[10rem]"
        height="100%"
        width="10rem"
      />
      <GradeFilter
        v-else-if="showGradeFilter"
        :grade-codes="header?.grades ?? []"
        :color="theme[50]"
      />
    </Teleport>
    <div class="grid-in-header-info flex flex-col items-start gap-4 self-center py-24 md:py-32">
      <h1 class="font-calistoga text-4xl" v-text="props.header?.title" />
      <article
        v-if="props.header?.intro"
        class="prose fluid-text-lg prose-p:my-0 hover:prose-a:text-seagreen-50 focus-visible:prose-a:ring space-y-2 font-medium text-[currentColor]"
      >
        <RichTextRenderer :text="props.header.intro" />
      </article>
      <HeaderTeacherButton
        v-if="showTeacherButton && props.header"
        :selected-header="props.header"
        :data-pendo="PendoFeature.TeacherContent.Header"
        color="teacher"
      />
    </div>
    <div
      :style="headerStyle"
      class="grid-in-image md:grid-in-image-top-full relative size-full"
    >
      <HeaderStencil
        v-if="header?.stencil?.identifier"
        :color="headerStyle"
        :stencil="header.stencil.identifier"
        class="absolute left-0 z-10"
      />
      <Image
        v-if="bgMedia"
        :class="!bgMediaIsLoading && 'animate-fade'"
        :content="bgMedia"
        class="aspect-video !h-auto min-h-full w-full max-w-full place-self-end object-cover md:h-0"
      />
    </div>
    <CopyrightButton
      v-if="bgMedia && 'copyright' in bgMedia && bgMedia.copyright"
      :copyright="bgMedia.copyright"
      class="col-end-[image] row-start-2 m-4 place-self-end"
    />
  </HeaderViewBannerGrid>
  <template v-if="isLoading">
    <section
      v-for="index in 2"
      :key="index"
      :style="sectionColors[index]"
    >
      <AuSkeletonWrapper class="max-w-screen-au mx-auto space-y-4 px-4 py-8 sm:px-8">
        <AuSkeleton
          :background="skeletonColor"
          height="36px"
          width="120px"
        />
        <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
          <AuSkeleton
            v-for="i in 4"
            :key="i"
            :background="skeletonColor"
            height="280px"
          />
        </ul>
      </AuSkeletonWrapper>
    </section>
  </template>
  <template v-else-if="header">
    <Header1To4Section
      v-for="(content, index) in packages"
      :id="`${index + 1}`"
      :key="content.contentId"
      :hash="route.hash"
      :grade-filter="showGradeFilter"
      :header="header"
      :index="index"
      :is-collapsible="isCollapsible"
      :section="content"
      :style="sectionColors[index]"
    />
  </template>
  <section
    v-if="contents.length > 0"
    :style="packages.length
      ? sectionColors[packages.length % sectionColors.length]
      : sectionColors
    "
  >
    <div class="max-w-screen-au mx-auto flex flex-col gap-4 px-4 pt-12 pb-16 sm:px-8">
      <h2
        v-if="packages.length > 0"
        class="font-calistoga fluid-text-2xl"
        v-text="t('labels.other')"
      />
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
        <li
          v-for="content in contents"
          :key="content.locationId"
        >
          <ColorCard :resource="content" />
        </li>
      </ul>
    </div>
  </section>
  <ScrollButton v-if="!isLoading" />
</template>

<style scoped>
:deep(.ks-dropdown .ks-dropdown-list ul) {
  max-height: 620px;
  font-size: 1.15rem;
}

#top-dropdowns :deep(input),
#top-dropdowns button {
  font-size: 0.9rem;
}

#top-dropdowns :deep(.ks-dropdown svg) {
  font-size: 0.9rem;
}

#top-dropdowns :deep(.ks-dropdown .ks-dropdown-list ul) {
  font-size: 0.9rem;
}

#header-filter :deep(input) {
  font-size: 1.4rem;
  padding: 0.6em;
  height: 2.1em;
  padding-right: 4rem !important;
  font-family: 'Calistoga', serif;
}
#header-filter :deep(svg) {
  top: 15px;
}

#header-filter :deep(button) {
  top: 12px;
}

@media (min-width: 900px) {
  #header-filter :deep(input) {
    font-size: 1.9rem;
    padding: 0.6em;
    height: 2.1em;
  }
  #header-filter :deep(svg) {
    top: 24px;
  }

  #header-filter :deep(button) {
    top: 21px;
  }
}

#header-filter :deep(input) {
  border: 1px solid v-bind(dropdownBorderColor);
  border-radius: 10px;
}

details::details-content {
  transition: content-visibility 0.3s allow-discrete;
}
</style>
